import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	EMAIL_DELIVERABILITY_FEATURE_AVAILABILITY,
	EmailDeliverabilityFeatureAvailability,
} from "@signalist/shared-types";

import { Toggle } from "@/components/Toggle/Toggle";
import { InputField } from "@/components/InputField/InputField";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Spinner } from "@/components/Spinner/Spinner";
import { useAppSelector } from "@/redux/hooks";

import {
	useGetContactSettingsQuery,
	useUpdateContactSettingsMutation,
} from "@/pages/Private/redux/contact/contact.api";
import { contactSelector } from "@/pages/Private/redux/contact/contact.slice";
import { ContactSettings as StoredContactSettings } from "@/pages/Private/pages/Contacts/schema/contacts";
import { saveErrorText } from "@/pages/Private/pages/Score/shell/saveErrorText";

import {
	changedDeliverabilityFields,
	deliverabilityValuesOf,
	EmailDeliverabilityValues,
} from "../utils/contactSettingsDeliverability";
import { EmailDeliverabilityCards } from "./EmailDeliverabilityCards";
import { ScopeBadge } from "./ScopeBadge";

interface ContactSettingsProps {
	/** Which deliverability cards have shipped; the shared-types constants unless a spec overrides. */
	featureAvailability?: EmailDeliverabilityFeatureAvailability;
}

/**
 * Company Settings → "Contacts & Enrichment" (PREC-4261): the workspace defaults. Auto-Archive and
 * Re-Enrichment behave as before; the email-deliverability cards render per their slice's constant.
 * A failed load shows Retry and no Save button; a failed save shows the server's reason below the
 * button and keeps every edit on screen.
 *
 * The draft re-seeds from the store only while it holds no edit: a save, failed or not, invalidates
 * the settings query, and the refetched values must not overwrite an unsaved draft (PREC-4261).
 * A successful save makes the server's answer the draft explicitly.
 */
export const ContactSettings: FunctionComponent<ContactSettingsProps> = ({
	featureAvailability = EMAIL_DELIVERABILITY_FEATURE_AVAILABILITY,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`contacts.settings.${key}`);

	const { isLoading, isError, refetch } = useGetContactSettingsQuery();
	const [updateSettings, { isLoading: isSaving }] = useUpdateContactSettingsMutation();
	const { settings } = useAppSelector(contactSelector);

	const [autoArchiveEnabled, setAutoArchiveEnabled] = useState(false);
	const [autoArchiveMonths, setAutoArchiveMonths] = useState<string>("");
	const [autoReEnrichEnabled, setAutoReEnrichEnabled] = useState(false);
	const [deliverability, setDeliverability] = useState<EmailDeliverabilityValues>(
		deliverabilityValuesOf(settings)
	);
	const [saveError, setSaveError] = useState<string | null>(null);
	const hasEditsRef = useRef(false);

	const seedDraft = useCallback((stored: StoredContactSettings) => {
		hasEditsRef.current = false;
		setAutoArchiveEnabled(stored.autoArchiveEnabled);
		setAutoArchiveMonths(stored.autoArchiveMonths?.toString() ?? "");
		setAutoReEnrichEnabled(stored.autoReEnrichEnabled);
		setDeliverability(deliverabilityValuesOf(stored));
	}, []);

	/** Runs a user's edit and marks the draft as holding one, so a refetch leaves it alone. */
	const edit = (apply: () => void) => {
		hasEditsRef.current = true;
		apply();
	};

	useEffect(() => {
		if (settings && !hasEditsRef.current) {
			seedDraft(settings);
		}
	}, [settings, seedDraft]);

	const handleSave = useCallback(async () => {
		setSaveError(null);

		try {
			const saved = await updateSettings({
				autoArchiveEnabled,
				autoArchiveMonths: autoArchiveMonths ? Number(autoArchiveMonths) : null,
				autoReEnrichEnabled,
				...changedDeliverabilityFields(deliverabilityValuesOf(settings), deliverability),
			}).unwrap();

			seedDraft(saved);
		} catch (error) {
			setSaveError(saveErrorText(error, t("contacts.settings.saveError")));
		}
	}, [
		autoArchiveEnabled,
		autoArchiveMonths,
		autoReEnrichEnabled,
		deliverability,
		seedDraft,
		settings,
		t,
		updateSettings,
	]);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<Spinner />
			</div>
		);
	}

	if (isError) {
		return (
			<div
				className="bg-brand-white rounded-lg border border-border-light p-6 flex items-center justify-between gap-4"
				data-testid="contact-settings-load-error"
				role="alert"
			>
				<p className="text-sm text-status-error">{ts("loadError")}</p>
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					size={ButtonSize.S}
					testId="contact-settings-retry"
					title={ts("retry")}
					onClick={() => void refetch()}
				/>
			</div>
		);
	}

	return (
		<div className="flex flex-col w-full">
			<EmailDeliverabilityCards
				availability={featureAvailability}
				values={deliverability}
				onChange={(patch) => edit(() => setDeliverability((current) => ({ ...current, ...patch })))}
			/>

			{/* Auto-Archive Card */}
			<div className="bg-brand-white rounded-lg border border-border-light p-6 mb-4">
				<div className="flex items-start justify-between">
					<div className="flex-grow mr-4">
						<div className="flex items-center gap-2 mb-1">
							<h4 className="text-sm font-semibold text-text">{ts("autoArchive.title")}</h4>
							<ScopeBadge label={ts("workspaceWide")} />
						</div>
						<p className="text-xs text-text-meta">{ts("autoArchive.description")}</p>
					</div>
					<Toggle
						isChecked={autoArchiveEnabled}
						testId="auto-archive-toggle"
						onChange={() => edit(() => setAutoArchiveEnabled(!autoArchiveEnabled))}
					/>
				</div>

				{autoArchiveEnabled && (
					<div className="mt-4 max-w-[200px]">
						<InputField
							label={ts("autoArchive.months")}
							name="autoArchiveMonths"
							placeholder={ts("autoArchive.monthsPlaceholder")}
							type="number"
							value={autoArchiveMonths}
							handleChange={(e) => edit(() => setAutoArchiveMonths(e.target.value))}
						/>
					</div>
				)}
			</div>

			{/* Re-Enrichment Card */}
			<div className="bg-brand-white rounded-lg border border-border-light p-6 mb-6">
				<div className="flex items-start justify-between">
					<div className="flex-grow mr-4">
						<div className="flex items-center gap-2 mb-1">
							<h4 className="text-sm font-semibold text-text">{ts("reEnrichment.title")}</h4>
							<ScopeBadge label={ts("workspaceWide")} />
						</div>
						<p className="text-xs text-text-meta">{ts("reEnrichment.description")}</p>
					</div>
					<Toggle
						isChecked={autoReEnrichEnabled}
						testId="re-enrichment-toggle"
						onChange={() => edit(() => setAutoReEnrichEnabled(!autoReEnrichEnabled))}
					/>
				</div>
			</div>

			{/* Save Button, and the server's reason below it when the save failed */}
			<div className="flex flex-col items-end gap-2">
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					isLoading={isSaving}
					size={ButtonSize.ML}
					testId="contact-settings-save"
					title={ts("save")}
					onClick={handleSave}
				/>
				{saveError && (
					<p
						className="text-xs text-status-error"
						data-testid="contact-settings-save-error"
						role="alert"
					>
						{saveError}
					</p>
				)}
			</div>
		</div>
	);
};
